import { type JSX } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import { type PdfBarChartData } from '../benchmarkingReport.types';
import { ReportPage } from '../components/ReportPage';
import {
  PDF_BORDER_RADIUS,
  PDF_FONT_WEIGHTS,
  PDF_FONTS,
  PDF_PAGE,
  PDF_SPACING,
  PDF_TEXT_STYLES,
  devOnly,
} from '../lib/pdfTokens';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEXT_PRIMARY = '#25252A';
const TEXT_SECONDARY = '#5E5E69';
const CARD_BG = '#FFFFFF';
const CARD_BORDER = '#D9D9E3';
const ANALYSIS_BG = '#C3F2DC';
const ANALYSIS_BOX_W = 180;
const CHART_W = 303;
const CHART_H = 210;

const FALLBACK_COMPARISON =
  'A sua escola apresenta desempenho superior a todas as médias do Enem, incluindo a média geral e as das escolas privadas (nacional, regional e municipal), evidenciando um trabalho pedagógico consistente. O foco estratégico passa a ser ampliar essa vantagem por meio de ações direcionadas às turmas e competências com maior potencial de impacto.';

const FALLBACK_CLASS_PERFORMANCE =
  'Comparando as turmas com a média da escola (840) e a meta (880), o 3° A se destaca como o melhor desempenho, com média 878, acima da média escolar e praticamente atingindo a meta. O 3° D também apresenta um resultado positivo, com 857, superando a média da escola, embora ainda abaixo da meta. Já as turmas 3° ano B (820) e 3° ano C (805) exigem intervenção pedagógica imediata.';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  titleBlock: { marginBottom: PDF_SPACING[1] },
  title: { ...PDF_TEXT_STYLES.pageTitle, marginBottom: 2 },
  subtitle: PDF_TEXT_STYLES.pageSubtitle,

  bodyRow: { flexDirection: 'row', gap: PDF_SPACING[6], marginBottom: PDF_SPACING[2] },
  bodyCol: { ...PDF_TEXT_STYLES.body, flex: 1 },

  card: {
    backgroundColor: CARD_BG,
    borderRadius: PDF_BORDER_RADIUS.md,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 2,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: CARD_BORDER,
    padding: PDF_SPACING[3],
    marginHorizontal: -PDF_PAGE.cardBleed,
    marginBottom: PDF_SPACING[2],
    flexDirection: 'row',
    gap: PDF_SPACING[3],
  },
  cardLeft: { flex: 1 },
  cardTitle: {
    fontSize: 12,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    marginBottom: 2,
    lineHeight: 1.35,
  },
  cardSubtitle: {
    fontSize: 9,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    lineHeight: 1.2,
    marginBottom: 24,
  },
  cardRight: {
    width: ANALYSIS_BOX_W,
    backgroundColor: ANALYSIS_BG,
    borderRadius: PDF_BORDER_RADIUS.sm,
    padding: PDF_SPACING[3],
  },
  analysisIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: PDF_SPACING[3],
  },
  analysisIconBox: {
    width: 24,
    height: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  analysisText: {
    fontSize: 8.5,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: '#00513D',
    lineHeight: 1.5,
  },
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type ChartCardProps = {
  title: string;
  subtitle: string;
  chartData?: PdfBarChartData;
  analysisFallback: string;
  lampIconUri?: string;
};

function ChartCard({ title, subtitle, chartData, analysisFallback, lampIconUri }: ChartCardProps): JSX.Element {
  const analysisText = chartData?.analise ?? devOnly(analysisFallback);

  return (
    <View
      style={styles.card}
      wrap={false}>
      <View style={styles.cardLeft}>
        <Text style={styles.cardTitle}>{title}</Text>
        <Text style={styles.cardSubtitle}>{subtitle}</Text>
        {chartData?.chartUri ? (
          <Image
            src={chartData.chartUri}
            style={{ width: CHART_W, height: CHART_H }}
          />
        ) : (
          <View style={{ width: CHART_W, height: CHART_H }} />
        )}
      </View>
      <View style={styles.cardRight}>
        {lampIconUri ? (
          <View style={styles.analysisIconRow}>
            <View style={styles.analysisIconBox}>
              <Image
                src={lampIconUri}
                style={{ width: 16, height: 16 }}
              />{' '}
            </View>
          </View>
        ) : null}
        <Text style={styles.analysisText}>{analysisText}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type PerformancePageProps = {
  performanceComparison?: PdfBarChartData;
  classPerformance?: PdfBarChartData;
  lampIconUri?: string;
};

export function PerformancePage({
  performanceComparison,
  classPerformance,
  lampIconUri,
}: PerformancePageProps): JSX.Element {
  return (
    <ReportPage>
      <View style={styles.titleBlock}>
        <Text style={styles.title}>De onde vem o resultado da escola</Text>
        <Text style={styles.subtitle}>A média geral é construída por dinâmicas internas de desempenho</Text>
      </View>

      <View style={styles.bodyRow}>
        <Text style={styles.bodyCol}>
          A média geral da escola é resultado da combinação do desempenho médio de todas as turmas de terceiro ano, cada
          uma contribuindo de forma distinta para o resultado consolidado.
        </Text>
        <Text style={styles.bodyCol}>
          Essa leitura transforma os dados de correção em insumos analíticos para orientar decisões e ações pedagógicas
          voltadas ao cumprimento da meta, identificando as turmas que impulsionam o resultado e as que demandam
          intervenções.
        </Text>
      </View>

      <ChartCard
        title='Comparativo de desempenho atual'
        subtitle='Posicionamento da escola em relação à média nacional do ENEM'
        chartData={performanceComparison}
        analysisFallback={FALLBACK_COMPARISON}
        lampIconUri={lampIconUri}
      />

      <ChartCard
        title='Desempenho das turmas em relação à meta'
        subtitle='Análise do desempenho médio de cada turma vs a meta da escola'
        chartData={classPerformance}
        analysisFallback={FALLBACK_CLASS_PERFORMANCE}
        lampIconUri={lampIconUri}
      />
    </ReportPage>
  );
}
